<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";
import UserStatusSelector from "@/components/parts/UserStatusSelector.vue";

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const isDropdownOpen = ref(false);
const dropdownRoot = ref(null);

const userRole = computed(() => store.getters.userData?.role?.name)
const isDistributor = computed(() => userRole.value === 'distributor')

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const handleClickOutside = (event) => {
  if (dropdownRoot.value && !dropdownRoot.value.contains(event.target)) {
    isDropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const isLoggedIn = computed(() => store.getters.isLoggedIn);
const userInfo = computed(() => store.getters.userData);
const allStatuses = computed(() => store.getters.allStatuses);

const currentStatusObject = computed(() => {
  const statusValue = userInfo.value?.currentStatus;

  if (!statusValue || !allStatuses.value.length) {
    return null;
  }

  const statusId = typeof statusValue === 'object' ? statusValue._id : statusValue;

  return allStatuses.value.find(s => s._id === statusId);
});

const logout = () => {
  store.dispatch('logout').then(() => {
    router.push('/login');
  });
};
</script>

<template>
  <div class="col d-flex gap-3 align-items-center justify-content-end text-end" v-if="isLoggedIn">
    <LanguageSwitcher class="me-2 custom-lang-switcher"/>
    <div v-if="userInfo" class="dropdown" ref="dropdownRoot">
      <a href="#" class="d-flex align-items-center link-dark text-decoration-none dropdown-toggle" @click.prevent="toggleDropdown">
        <div class="d-flex flex-column align-items-end">
          <strong>{{ userInfo.username }}</strong>
          <small v-if="currentStatusObject" class="d-flex align-items-center" :style="{ color: currentStatusObject.color }">
            <span class="status-dot-small me-1" :style="{ backgroundColor: currentStatusObject.color }"></span>
            {{ currentStatusObject.name }}
          </small>
        </div>
      </a>
      <ul class="dropdown-menu dropdown-menu-end text-small shadow" :class="{ 'show': isDropdownOpen }">
        <li>
          <h6 class="dropdown-header">{{ userInfo.role?.name }}</h6>
        </li>
        <li class="p-2" v-if="!isDistributor">
          <UserStatusSelector v-if="store.state.isInitialDataLoaded && (store.getters.isManager || store.getters.isDraftsman)"/>
        </li>
        <li><hr class="dropdown-divider"></li>
        <li>
          <a class="dropdown-item text-danger" href="#" @click.prevent="logout">
            {{ t('buttons.logoutBtn') }}
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
#userInfo {
  position: relative;
  text-transform: capitalize;

  small {
    text-transform: lowercase;
  }
}

small {
  font-weight: lighter;
}

.dropdown-menu.show {
  display: block;
}

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dropdown-header {
  font-size: .8rem;
  text-transform: uppercase;
  color: #6c757d;
}

.dropdown-toggle::after {
  margin-left: .5em;
}

a.dropdown-toggle {
  padding: 0;
  background: none;
  border: none;
  line-height: 1.2;
}

.custom-lang-switcher {
  :deep(.dropdown),
  :deep(.dropdown-menu) {
    z-index: 9999;
  }
}
</style>